import { azureStorageChipsToOData, stampAzureStorageChipTags } from '../../../core/engines/azureStorageOData';
import type { TableExplorerSnapshot, UiTableFilterGroup } from '../../../types/mcpUiControl';
import type { FilterGroup, StorageExplorerEntityView, StorageExplorerTab } from '../../workspace/core/types/workspaceTypes';
import { DEFAULT_ENTITY_VIEW } from '../../workspace/storage-explorer/entities/model/entityViewState';
import { useEntityTabRuntimeStore } from '../../workspace/storage-explorer/entities/model/entityTabRuntimeStore';

const serializeRow = (row: Record<string, unknown>): Record<string, unknown> => Object.fromEntries(
  Object.entries(row).map(([key, value]) => [key, typeof value === 'bigint' ? value.toString() : value instanceof Date ? value.toISOString() : value]),
);

const filtersFromInput = (filters: UiTableFilterGroup[]): FilterGroup[] => filters.map((group) => ({
  field: group.field,
  conditions: group.conditions.map((condition) => ({ operator: condition.operator, value: condition.value, value2: condition.value2 })),
}));

const snapshotValue = (value: FilterGroup['conditions'][number]['value']): string | number | boolean | null | undefined => {
  if (value === undefined || value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value, (_key, item: unknown) => typeof item === 'bigint' ? item.toString() : item);
};

const filtersForSnapshot = (filters: FilterGroup[]): UiTableFilterGroup[] => filters.map((group) => ({
  field: group.field,
  conditions: group.conditions.map((condition) => ({
    operator: condition.operator,
    value: snapshotValue(condition.value),
    value2: condition.value2 instanceof Date
      ? condition.value2.toISOString()
      : typeof condition.value2 === 'bigint'
        ? condition.value2.toString()
        : condition.value2,
  })),
}));

export function setEntityTabFilter(
  tab: StorageExplorerTab,
  input: UiTableFilterGroup[],
): { success: true; entityView: StorageExplorerEntityView } | { success: false; error: string } {
  if (tab.content.mode !== 'entities' || tab.content.resource.kind !== 'table') return { success: false, error: 'Can only set filters on Azure Table entity tabs' };
  const runtime = useEntityTabRuntimeStore.getState().entries[tab.id];
  // Agent-set chips carry operand tags like wizard chips, so a restart keeps
  // their EDM types (S4 review C, M1).
  const filters = stampAzureStorageChipTags(filtersFromInput(input), runtime?.propertyTypes ?? {});
  try {
    if (filters.length > 0) azureStorageChipsToOData(filters, runtime?.propertyTypes ?? {});
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Invalid Azure Table filter' };
  }
  if (runtime) {
    useEntityTabRuntimeStore.getState().publish(tab.id, {
      ...runtime,
      rows: [],
      currentPage: 1,
      loading: true,
      error: undefined,
    });
  }
  return {
    success: true,
    entityView: { ...(tab.content.entityView ?? DEFAULT_ENTITY_VIEW), filters, odataFilter: undefined, filterMode: 'wizard' },
  };
}

export function entityTabAsTableExplorerView(tab: StorageExplorerTab, maxPreviewRows = 10): TableExplorerSnapshot | null {
  if (tab.content.mode !== 'entities' || tab.content.resource.kind !== 'table') return null;
  const view = tab.content.entityView ?? DEFAULT_ENTITY_VIEW;
  const runtime = useEntityTabRuntimeStore.getState().entries[tab.id];
  const loading = runtime?.loading ?? true;
  return {
    tabId: tab.id,
    table: { database: 'storage-tables', schema: 'storage', name: tab.content.resource.table },
    connectionId: tab.content.connectionId,
    columns: (runtime?.columns ?? []).map((column) => ({
      name: column.name,
      dataType: column.dataType,
      nullable: column.nullable,
      isPrimaryKey: column.isPrimaryKey,
      isForeignKey: column.isForeignKey,
    })),
    filters: view.filterMode === 'wizard' ? filtersForSnapshot(view.filters) : [],
    filterState: view.filterMode === 'odata'
      ? { mode: 'odata', ...(view.odataFilter ? { odataFilter: view.odataFilter } : {}), loading }
      : { mode: 'wizard', filters: filtersForSnapshot(view.filters), loading },
    sorting: runtime?.sort ? [{ field: runtime.sort.column, direction: runtime.sort.direction }] : [],
    pagination: { pageSize: view.pageSize, currentPage: runtime?.currentPage ?? 1 },
    previewRows: (runtime?.rows ?? []).slice(0, maxPreviewRows).map(serializeRow),
    isLoading: loading,
    ...(runtime?.error ? { error: runtime.error } : {}),
  };
}
